import { supabase } from "../supabase/SupabaseClient";
import { enElRango } from "../shared/periodoVales";

/* Los vales de GAS OIL del surtidor de la base, como los registró el bot.
 *
 * Mismo criterio que `services/urea`: ESTA PANTALLA NO CARGA. El alta es la
 * foto del vale por WhatsApp —lectura, confirmación con SI, cotejo con el
 * padrón— y una segunda puerta acá tendría otras reglas. El hub mira, borra
 * (`admin.borrar`) y, con `editar.vale_gasoil`, corrige el número leído mal y
 * anula números del talonario. Todo eso lo decide la RLS / el RPC: la pantalla
 * sólo lo cuenta.
 *
 * ── SI LA MIGRACIÓN TODAVÍA NO SE APLICÓ ───────────────────────────────────
 *
 * La tabla nace con `20261007200000_gasoil_de_la_base.sql`. Hasta que esté
 * aplicada, PostgREST contesta que la tabla no existe; eso no es «no hay
 * cargas» ni «se cayó la base», y se dice distinto (`FaltaMigracion`).
 */

const CAMPOS =
  "id,fecha,vale,serie,litros,chofer,chofer_dni,chofer_padron,patente," +
  "interno,unidad_tipo,referencia,km,importe,despacho,quien,quien_nombre," +
  "fuente,foto,media_id,nota,creado_en,vale_editado_por,vale_editado_en," +
  "vale_original";

export class FaltaMigracion extends Error {
  constructor() {
    super("Todavía no está creada la tabla de gasoil de la base.");
    this.name = "FaltaMigracion";
  }
}

/* PGRST205: tabla que no está en el caché de esquema · 42P01: no existe ·
 * PGRST202: función que no existe. */
export const esFaltaMigracion = (error) =>
  Boolean(error) &&
  (["PGRST205", "PGRST202", "42P01", "42883"].includes(error.code) ||
    /could not find the (table|function)|does not exist/i.test(error.message || ""));

const lanzar = (error) => {
  if (esFaltaMigracion(error)) throw new FaltaMigracion();
  throw error;
};

/* El rango [desde, hasta], ambos inclusive, en días argentinos. El tope de
 * 999 es el techo real de una página de PostgREST: un mes de vales anda por
 * las decenas o pocas centenas. */
export async function traerCargasGasoil(desde, hasta) {
  const q = enElRango(
    supabase
      .from("gasoil_cargas")
      .select(CAMPOS)
      .order("fecha", { ascending: false })
      .limit(999),
    desde, hasta);
  const { data, error } = await q;
  if (error) lanzar(error);
  return data || [];
}

/* Borrar de verdad la carga mal registrada (el vale doble, la foto repetida).
 * El `.select()` confirma: sin permiso la RLS filtra en silencio y «borra»
 * cero filas, y cero filas acá ES el error. */
export async function borrarCargaGasoil(id) {
  const { data, error } = await supabase
    .from("gasoil_cargas")
    .delete()
    .eq("id", id)
    .select("id");
  if (error) lanzar(error);
  if (!data || data.length === 0)
    throw new Error("No se borró nada: no tenés permiso.");
  return data[0];
}

/* Corregir el número que el bot leyó mal. La foto no se toca; queda registro
 * de quién y cuándo. Si el número ya está en otra carga, el RPC lo rechaza. */
export async function editarValeGasoil(id, vale) {
  const { data, error } = await supabase.rpc("editar_vale_gasoil", {
    p_id: id,
    p_vale: vale,
  });
  if (error) lanzar(error);
  return Array.isArray(data) ? data[0] : data;
}

/* Los números anulados o sin usar que siguen vigentes. Sin la tabla (o sin
 * poder leerla) devuelve `null`: NO es «no hay anulados», y quien llama no
 * saca nada de la lista de faltantes. */
export async function traerAnuladosGasoil() {
  const { data, error } = await supabase
    .from("gasoil_vales_anulados")
    .select("id,numero,motivo,nota,quien,creado_en")
    .is("revertido_en", null)
    .order("numero", { ascending: true })
    .limit(999);
  if (error) return null;
  return data || [];
}

/* Anotar números que no van a aparecer nunca. Devuelve los que anotó: los que
 * ya tenían carga o ya estaban anulados no vuelven, y la pantalla lo dice. */
export async function anularValesGasoil(numeros, motivo, nota = "") {
  const { data, error } = await supabase.rpc("anular_vales_gasoil", {
    p_numeros: numeros,
    p_motivo: motivo,
    p_nota: nota || null,
  });
  if (error) lanzar(error);
  return data || [];
}

export async function revertirValeGasoil(numero) {
  const { data, error } = await supabase.rpc("revertir_vale_gasoil", {
    p_numero: numero,
  });
  if (error) lanzar(error);
  return data || [];
}
